import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Edit, Download, Share2, CalendarClock, Sparkles, RefreshCw,
  Star, ChevronRight, Loader2, AlertTriangle, ArrowLeft, Home,
  Workflow, ChevronDown, Play,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { modeMeta } from '../components/ScreenFlowsPanel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { formatAggValue, displayLabel, presetLabel, ME_TOKEN } from '../../analytics-shared/engine/reportEngine';
import { useQuery, invalidateQueries } from '../../analytics-shared/hooks/queryShim';
import { reportsApi, createEmptyFilter, uid } from '../api/reportsApi';
import { useObject } from '../../analytics-shared/hooks/useFieldCatalog';
// useRecords removed — record fetch is server-side now (inside the
// /reports/run engine endpoint), so the page no longer pulls records
// directly. The interactive RunResult includes the filtered record set.
// exportsApi import removed — wedge-5 export flow now lives behind the
// ReportExportModal mounted at the page root.
import { getInsights } from '../ai/aiAssistant';
import ReportTable from '../components/ReportTable';
import ChartRenderer from '../../analytics-shared/components/ChartRenderer';
import ScheduleReportModal from '../components/modals/ScheduleReportModal';
import ShareModal from '../../analytics-shared/components/ShareModal';
import ReportExportModal from '../../analytics-shared/components/ReportExportModal';
import { ASSET_REPORT } from '../../analytics-shared/api/sharesApi';

const OPERATOR_LABEL = {
  equals: '=', not_equals: '≠', contains: 'contains', not_contains: "doesn't contain",
  starts_with: 'starts with', ends_with: 'ends with',
  is_one_of: 'is any of', is_none_of: 'is none of',
  includes_any: 'includes any of', includes_all: 'includes all of', excludes: 'excludes',
  is_empty: 'is empty', is_not_empty: 'is not empty',
  gt: '>', gte: '≥', lt: '<', lte: '≤',
  date_preset: 'in', before: 'before', after: 'after', between: 'between',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const buildFilterLabel = (condition, fieldMap) => {
  const field = fieldMap[condition.field] || { label: condition.field };
  const op = OPERATOR_LABEL[condition.operator] || condition.operator;
  const labels = condition.valueLabels || {};
  const pretty = (v) => (v === ME_TOKEN ? 'Me' : (labels[v] || v));
  let value = Array.isArray(condition.value)
    ? condition.value.map(pretty).join(', ')
    : pretty(condition.value);
  if (condition.operator === 'date_preset') value = presetLabel(condition.value);
  // displayLabel adds the "(Lead)" / "(Contact)" qualifier on related
  // fields so a filter chip on Lead.City reads "City (Lead) = Boston"
  // instead of an ambiguous "City = Boston".
  return `${displayLabel(field) || field.label} ${op} ${value}`;
};

const flattenFilterTree = (node, fieldMap = {}) => {
  if (!node) return [];
  if (node.type === 'condition') {
    return [{ id: node.id, label: buildFilterLabel(node, fieldMap), field: node.field, value: node.value }];
  }
  if (!Array.isArray(node.children)) return [];
  return node.children.flatMap((child) => flattenFilterTree(child, fieldMap));
};

const mergeFilters = (base, extra) => {
  const root = base && base.type === 'group' ? base : createEmptyFilter();
  if (!Array.isArray(extra) || !extra.length) return root;
  if (!Array.isArray(root.children)) return { ...root, children: extra };
  if (root.logic === 'AND') return { ...root, children: [...root.children, ...extra] };
  return { id: uid('grp'), type: 'group', logic: 'AND', children: [...root.children, ...extra] };
};

const ReportViewPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [report, setReport] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeChartFilter, setActiveChartFilter] = useState(null);
  const [showInsights, setShowInsights] = useState(true);

  useEffect(() => {
    let cancelled = false;
    reportsApi.get(reportId).then((loaded) => {
      if (cancelled) return;
      if (!loaded) {
        toast.error('Report not found');
        navigate('/reports');
        return;
      }
      setReport(loaded);
      // Stamp Recent on every successful page entry so the sidebar's
      // Recent tab actually populates. Best-effort: failures here
      // shouldn't block rendering — the user can still see the report.
      reportsApi.markRun(reportId).catch(() => {});
    });
    return () => { cancelled = true; };
  }, [reportId, navigate]);

  const config = useMemo(() => report || null, [report]);

  // Live object metadata. The fixture lookup was returning null for every
  // real tenant object key (e.g. "account", "lead", "contact") because the
  // fixtures only know about ten demo keys, which stranded the viewer in a
  // permanent "Loading report..." state.
  const {
    data: object,
    isLoading: objectLoading,
    error: objectError,
    refetch: refetchObject,
  } = useObject(config?.object);

  const fieldMap = useMemo(() => {
    if (!object?.fields) return {};
    return object.fields.reduce((acc, f) => { acc[f.key] = f; return acc; }, {});
  }, [object]);

  // Build the effective config — overlay the active chart drill-down
  // filter on top of the saved config if one is set.
  const interactiveConfig = useMemo(() => {
    if (!config) return null;
    if (!activeChartFilter?.field) return config;
    const extraCondition = {
      id: uid('c'), type: 'condition', field: activeChartFilter.field,
      operator: 'equals', value: activeChartFilter.value,
    };
    return { ...config, filters: mergeFilters(config.filters, [extraCondition]) };
  }, [config, activeChartFilter]);

  // After wedge 5 execution lives server-side. We POST the interactive
  // config (saved + overlay chart filter) to /reports/run and consume
  // the RunResult dict directly. Key on a JSON hash of the config so
  // toggling chart filters invalidates the cache.
  const interactiveKey = useMemo(
    () => JSON.stringify(interactiveConfig || {}),
    [interactiveConfig],
  );
  const runQuery = useQuery({
    queryKey: ['reports', 'view-run', interactiveConfig?.id, interactiveKey],
    queryFn: () => reportsApi.runConfig(interactiveConfig),
    enabled: !!interactiveConfig?.object,
    staleTime: 15_000,
  });
  const result = runQuery.data || null;
  const records = result?.records || [];
  const recordsError = runQuery.error;
  const refetchRecords = runQuery.refetch;

  const filterChips = useMemo(() => flattenFilterTree(config?.filters, fieldMap), [config?.filters, fieldMap]);
  const activeFilterChip = activeChartFilter
    ? [{
      id: 'active-filter',
      label: `${displayLabel(fieldMap[activeChartFilter.field]) || activeChartFilter.field} = ${activeChartFilter.value}`,
    }]
    : [];
  const hasActiveFilters = filterChips.length > 0 || activeFilterChip.length > 0;

  const insights = useMemo(() => (result && config ? getInsights(result, config, fieldMap) : []), [result, config, fieldMap]);

  const summaryCards = useMemo(() => {
    if (!result || !config) return [];
    const keys = Object.keys(config.summaries || {});
    if (!keys.length) {
      return [{ id: 'count', label: 'Record Count', value: result.recordCount, delta: '+0%' }];
    }
    return keys.map((fieldKey) => {
      const field = fieldMap[fieldKey] || { label: fieldKey };
      const value = result.grandTotals[fieldKey];
      return { id: fieldKey, label: `${displayLabel(field) || field.label} (${config.summaries[fieldKey]})`, value };
    });
  }, [config, fieldMap, result]);

  const handleEdit = useCallback(() => navigate(`/reports/builder/${reportId}`), [navigate, reportId]);

  const handleRun = useCallback(() => {
    if (!report) return;
    setRefreshing(true);
    (async () => {
      try {
        await Promise.all([
          refetchRecords(),
          reportsApi.markRun(report.id),
        ]);
        const updated = await reportsApi.get(report.id);
        setReport(updated);
        toast.success('Report refreshed');
      } catch (err) {
        toast.error(err?.message || 'Failed to refresh report');
      } finally {
        setRefreshing(false);
      }
    })();
  }, [report, refetchRecords]);

  // Surface record-fetch failures so users know why the report looks empty.
  useEffect(() => {
    if (recordsError) {
      toast.error(`Couldn't load records: ${recordsError.message || 'Unknown error'}`);
    }
  }, [recordsError]);

  // Open the format-picker modal. The actual export call goes through
  // exportsApi.exportReport() inside ReportExportModal — that hits the
  // wedge-5 backend export endpoint which runs the engine against the
  // latest CRM data, so the previewed numbers and the downloaded file
  // can't drift.
  const handleExport = useCallback(() => {
    if (!report?.id) {
      toast.error('Save the report before exporting.');
      return;
    }
    setExportOpen(true);
  }, [report]);

  // Pre-wedge-3 this just copied the page URL — kept as a fallback for
  // anonymous/embed contexts (no report.id yet) but the primary action
  // now opens the real share-management modal.
  const handleShare = useCallback(async () => {
    if (report?.id) {
      setShareOpen(true);
      return;
    }
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Report link copied to clipboard');
    } catch {
      toast('Copy link manually', { icon: '🔗' });
    }
  }, [report]);

  const handleFilterClear = useCallback(() => setActiveChartFilter(null), []);

  // ScheduleReportModal now owns the save call (PUT /v1/reports/:id/schedule
  // via reportsApi.saveSchedule / removeSchedule). We just receive the
  // post-save schedule blob (or null on remove), patch local state, and
  // bust the list cache so the schedule badge updates on the list page
  // without a reload.
  const handleScheduleSave = useCallback((scheduleBlob) => {
    if (!report) return;
    setReport({ ...report, schedule: scheduleBlob || null });
    invalidateQueries(['reports']);
  }, [report]);

  // Same pattern for the favorite toggle on this page — calls
  // reportsApi directly. Without invalidation, the list page's
  // ⭐ chip / Favorites tab counter stay out of sync.
  const handleFavorite = useCallback(async () => {
    if (!report) return;
    await reportsApi.toggleFavorite(report.id);
    setReport(await reportsApi.get(report.id));
    invalidateQueries(['reports']);
  }, [report]);

  // ── Screen Flow launcher ──────────────────────────────────────────
  // Attached flows (saved on the report) become run-page actions:
  //   basic         → launch immediately
  //   record_detail → pick ONE record, passes recordId
  //   list_view     → pick records or All, passes selectedRecordIds
  const [flowDialog, setFlowDialog] = useState(null); // {flow, mode: 'record'|'list'}
  const [flowSelection, setFlowSelection] = useState(new Set());
  const [flowAllRecords, setFlowAllRecords] = useState(true);

  // Unique primary records from the result (joins fan rows out — dedupe
  // by primary id so each record appears once in the picker).
  const primaryRecords = useMemo(() => {
    const seen = new Set();
    const out = [];
    (result?.records || []).forEach((rec) => {
      if (!rec?.id || seen.has(rec.id)) return;
      seen.add(rec.id);
      out.push(rec);
    });
    return out;
  }, [result]);

  const recordLabel = useCallback((rec) => {
    const first = rec.first_name || '';
    const last = rec.last_name || '';
    if (first || last) return `${first} ${last}`.trim();
    return rec.name || rec.account_name || rec.subject || rec.title || rec.email || rec.id;
  }, []);

  const launchFlow = useCallback((flow, recordIds) => {
    const params = new URLSearchParams();
    params.set('object', config?.object || '');
    if (flow.launchMode === 'record_detail' && recordIds?.length) {
      params.set('recordId', recordIds[0]);
    } else if (flow.launchMode === 'list_view') {
      params.set('selectedRecordIds', (recordIds || []).join(','));
    }
    window.open(`/flow-runner/${flow.flowId}?${params.toString()}`, '_blank');
    setFlowDialog(null);
  }, [config]);

  const handleFlowClick = useCallback((flow) => {
    if (flow.launchMode === 'record_detail') {
      setFlowSelection(new Set());
      setFlowDialog({ flow, mode: 'record' });
    } else if (flow.launchMode === 'list_view') {
      setFlowSelection(new Set());
      setFlowAllRecords(true);
      setFlowDialog({ flow, mode: 'list' });
    } else {
      launchFlow(flow, []);
    }
  }, [launchFlow]);

  const toggleFlowRecord = (id) => {
    setFlowSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (searchParams.get('run') === '1' && report) {
      setTimeout(() => handleRun(), 200);
    }
  }, [searchParams, report, handleRun]);

  // Three-state load: config fetch → schema fetch → render. Without this
  // split a missing live object would silently leave the viewer on
  // "Loading report..." forever.
  if (!config) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (objectLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-slate-50 gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
        <p className="text-sm text-slate-500">Loading {config.object} schema…</p>
      </div>
    );
  }
  if (objectError || !object) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50 px-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-md text-center">
          <div className="mx-auto w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center mb-3">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
          </div>
          <h3 className="text-base font-semibold text-slate-900">
            {objectError ? "Couldn't load CRM schema" : 'Object not available'}
          </h3>
          <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
            {objectError
              ? (objectError.message || 'Failed to fetch object metadata')
              : `This report points at "${config.object}", which isn't present in the current tenant schema.`}
          </p>
          <div className="flex items-center justify-center gap-2 mt-5">
            <Button variant="outline" size="sm" onClick={() => navigate('/reports')}>
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Reports
            </Button>
            {objectError && <Button size="sm" onClick={refetchObject}>Retry</Button>}
          </div>
        </div>
      </div>
    );
  }
  if (!result) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50">
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto max-w-[1480px] px-6 py-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <button onClick={() => navigate('/crm-platform')} className="flex items-center gap-1 font-medium text-slate-500 hover:text-indigo-600">
                <Home className="h-3.5 w-3.5" /> CRM
              </button>
              <ChevronRight className="h-3.5 w-3.5" />
              <button onClick={() => navigate('/reports')} className="hover:text-slate-900">Reports</button>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="font-medium text-slate-700 truncate max-w-[260px]">{config.name}</span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">{config.name}</h1>
                <div className="mt-1 flex flex-wrap gap-2 text-sm text-slate-500">
                  <span>{object?.label || 'Report'}</span>
                  <span>• Last run {fmtDate(report.lastRun)}</span>
                  {/* Status copy is schedule-aware. Without a schedule
                      we read "On-demand" so users don't confuse the
                      absence of auto-execution with "the scheduler is
                      broken". With a paused schedule we say so. With
                      an active schedule we surface the next-run time
                      so the user can see WHEN the next firing is. */}
                  <span>• {(() => {
                    if (refreshing) return 'Refreshing now';
                    const s = report.schedule;
                    if (!s) return 'On-demand only';
                    if (s.paused) return 'Schedule paused';
                    if (s.next_run) {
                      const nextRun = new Date(s.next_run);
                      if (!Number.isNaN(nextRun.getTime())) {
                        return `Next run ${nextRun.toLocaleString()}`;
                      }
                    }
                    return `Scheduled ${s.frequency || 'daily'}`;
                  })()}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {(report?.screenFlows || []).length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="border-violet-200 text-violet-700 hover:bg-violet-50">
                    <Workflow className="h-4 w-4 mr-1" /> Flows <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {(report.screenFlows || []).map((f) => {
                    const meta = modeMeta(f.launchMode);
                    return (
                      <DropdownMenuItem key={f.flowId} onClick={() => handleFlowClick(f)}>
                        <Play className="h-3.5 w-3.5 mr-2 text-violet-500" />
                        <span className="flex-1">{f.name}</span>
                        <span className="text-[10px] text-slate-400 ml-2">{meta.label}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button size="sm" variant="outline" onClick={handleShare}>
              <Share2 className="h-4 w-4 mr-1" /> Share
            </Button>
            <Button size="sm" variant="outline" onClick={() => setScheduleOpen(true)}>
              <CalendarClock className="h-4 w-4 mr-1" /> Subscribe
            </Button>
            <Button size="sm" variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-1" /> Export
            </Button>
            <Button size="sm" variant="outline" onClick={handleRun}>
              <RefreshCw className="h-4 w-4 mr-1" /> {refreshing ? 'Running…' : 'Run'}
            </Button>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleEdit}>
              <Edit className="h-4 w-4 mr-1" /> Edit
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1480px] px-6 py-6 space-y-6">
        <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {summaryCards.map((card) => (
                <div key={card.id} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-400">{card.label}</div>
                  <div className="mt-4 text-3xl font-semibold text-slate-900">{formatAggValue(card.value, card.id, config.summaries?.[card.id] || 'COUNT', fieldMap)}</div>
                  <div className="mt-2 text-xs text-slate-500">
                    across {(result?.recordCount || 0).toLocaleString()} rows
                  </div>
                </div>
              ))}
            </div>

            <div className="grid gap-4">
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">Charts</h2>
                    <p className="text-sm text-slate-500">Visual analytics built from this report configuration.</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setShowInsights((v) => !v)}>
                    <Sparkles className="h-4 w-4 mr-2" /> Insights
                  </Button>
                </div>
                {config.charts.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-6 py-14 text-center text-slate-500">
                    No charts configured. Click Edit to add charts and visual summaries.
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {config.charts.map((chart) => {
                      // Engine pre-computes chart data per saved chart on
                      // every run. Look it up by chart id; an empty array
                      // is fine while a refresh is in flight.
                      const chartEntry = (result.chartData || []).find((c) => c?.id === chart.id);
                      const data = chartEntry?.data || [];
                      return (
                        <div key={chart.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                          <div className="mb-3 flex items-center justify-between gap-3">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">{chart.title || 'Chart'}</div>
                              <div className="text-xs text-slate-500">{object?.fields.find((f) => f.key === chart.groupField)?.label || chart.groupField} · {chart.agg}</div>
                            </div>
                            <Badge variant="outline" className="text-xs text-slate-500 border-slate-200 bg-white">{chart.type}</Badge>
                          </div>
                          <ChartRenderer
                            type={chart.type}
                            data={data}
                            seriesKeys={chartEntry?.seriesKeys || null}
                            stacked={!!chart.stacked}
                            stack100={!!chart.stack100}
                            goalValue={chartEntry?.goalValue}
                            colorRules={chart.colorRules || null}
                            onSliceClick={(name) => setActiveChartFilter((prev) => (
                              prev && prev.field === chart.groupField && prev.value === name
                                ? null
                                : { field: chart.groupField, value: name }
                            ))}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Report Data</h2>
                  <p className="text-sm text-slate-500">Live records, grouped rows and grand totals.</p>
                </div>
                <Badge variant="outline" className="text-xs rounded-full border-slate-200 bg-slate-100 text-slate-700">
                  {(result?.recordCount || 0).toLocaleString()} rows
                </Badge>
              </div>
              {result?.meta?.truncated && (
                <div className="mb-3 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-2.5 text-sm text-indigo-700">
                  Showing the first {(result.meta.displayedRows || 0).toLocaleString()} rows of{' '}
                  {(result.recordCount || 0).toLocaleString()}.
                  {result.meta.exactTotals !== false
                    ? ' Totals, counts and charts reflect ALL rows.'
                    : ' Totals are computed over a bounded scan — narrow your filters for exact numbers.'}
                </div>
              )}
              <div className="h-[620px] overflow-hidden rounded-3xl border border-slate-200">
                <ReportTable config={config} result={result} fieldMap={fieldMap} readOnly paged onSort={() => {}} />
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Report details</h2>
                  <p className="text-sm text-slate-500">Metadata, schedule and access.</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleFavorite}>
                  <Star className={`h-4 w-4 ${report.favorite ? 'text-amber-400' : 'text-slate-400'}`} />
                </Button>
              </div>
              <div className="space-y-3 text-sm text-slate-600">
                <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Created by</span><span>{report.createdBy}</span></div>
                <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Created</span><span>{fmtDate(report.createdAt)}</span></div>
                <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Last modified</span><span>{fmtDate(report.updatedAt)}</span></div>
                <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Last run</span><span>{fmtDate(report.lastRun)}</span></div>
                <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Folder</span><span>{report.folder || 'Unfiled'}</span></div>
                <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Visibility</span><span>{report.visibility}</span></div>
                <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Schedule</span><span>{report.schedule ? `${report.schedule.frequency} / ${report.schedule.format}` : 'None'}</span></div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Filters</h2>
                  <p className="text-sm text-slate-500">Active conditions applied to the report.</p>
                </div>
                <Button variant="ghost" size="sm" onClick={handleFilterClear}>Clear</Button>
              </div>
              <div className="space-y-2">
                {!hasActiveFilters ? (
                  <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-slate-400">
                    No filters are active. Use the report builder to add filter logic.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {filterChips.map((chip) => (
                      <Badge key={chip.id} variant="secondary" className="bg-slate-100 text-slate-700">{chip.label}</Badge>
                    ))}
                    {activeFilterChip.map((chip) => (
                      <Badge key={chip.id} className="bg-indigo-50 text-indigo-700 border-indigo-200">{chip.label}</Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-6 space-y-3">
                <div className="rounded-2xl bg-slate-50 p-3 text-sm text-slate-500">
                  <div className="font-medium text-slate-700">Group By</div>
                  <div>{config.groupBy.length ? config.groupBy.map((key) => fieldMap[key]?.label || key).join(' • ') : 'No grouping configured'}</div>
                </div>
                <div className="rounded-2xl bg-slate-50 p-3 text-sm text-slate-500">
                  <div className="font-medium text-slate-700">Sort</div>
                  <div>{config.sort ? `${fieldMap[config.sort.field]?.label || config.sort.field} ${config.sort.dir}` : 'Default order'}</div>
                </div>
              </div>
            </div>

            {showInsights && (
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">AI Insights</h2>
                    <p className="text-sm text-slate-500">Automated observations and suggested focus areas.</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setShowInsights(false)}>Hide</Button>
                </div>
                <div className="space-y-3">
                  {insights.map((ins) => (
                    <div key={ins.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-xs uppercase tracking-[0.12em] text-slate-500">{ins.title}</div>
                      <div className="mt-2 text-sm text-slate-700">{ins.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      <Dialog open={!!flowDialog} onOpenChange={(v) => !v && setFlowDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Workflow className="h-4 w-4 text-violet-600" /> Run "{flowDialog?.flow?.name}"
            </DialogTitle>
            <DialogDescription>
              {flowDialog?.mode === 'record'
                ? 'Choose the record this flow should run on.'
                : 'Choose which records to send to the flow — their IDs are passed as the recordIds variable.'}
            </DialogDescription>
          </DialogHeader>

          {flowDialog?.mode === 'list' && (
            <div className="flex rounded-lg overflow-hidden border border-slate-200 mb-2">
              {[[true, `All records (${primaryRecords.length})`], [false, 'Select specific records']].map(([v, label]) => (
                <button key={String(v)} type="button" onClick={() => setFlowAllRecords(v)}
                  className={`flex-1 px-2 py-2 text-xs font-semibold transition-colors ${
                    flowAllRecords === v ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {(flowDialog?.mode === 'record' || (flowDialog?.mode === 'list' && !flowAllRecords)) && (
            <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-slate-100">
              {primaryRecords.length === 0 && (
                <p className="text-sm text-slate-400 p-4 text-center">No records in this report.</p>
              )}
              {primaryRecords.map((rec) => {
                const on = flowSelection.has(rec.id);
                return (
                  <button key={rec.id} type="button"
                    onClick={() => {
                      if (flowDialog.mode === 'record') setFlowSelection(new Set([rec.id]));
                      else toggleFlowRecord(rec.id);
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                      on ? 'bg-violet-50' : 'hover:bg-slate-50'
                    }`}>
                    <span className={`w-4 h-4 rounded ${flowDialog.mode === 'record' ? 'rounded-full' : ''} border flex items-center justify-center flex-shrink-0 ${
                      on ? 'bg-violet-600 border-violet-600' : 'border-slate-300'
                    }`}>
                      {on && <span className="w-1.5 h-1.5 bg-white rounded-full" />}
                    </span>
                    <span className="text-sm text-slate-700 truncate">{recordLabel(rec)}</span>
                  </button>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFlowDialog(null)}>Cancel</Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700"
              disabled={
                flowDialog?.mode === 'record'
                  ? flowSelection.size !== 1
                  : flowDialog?.mode === 'list' && !flowAllRecords && flowSelection.size === 0
              }
              onClick={() => {
                const ids = flowDialog.mode === 'list' && flowAllRecords
                  ? primaryRecords.map((r) => r.id)
                  : [...flowSelection];
                launchFlow(flowDialog.flow, ids);
              }}>
              <Play className="h-4 w-4 mr-1.5" />
              Run Flow
              {flowDialog?.mode === 'list' && !flowAllRecords && flowSelection.size > 0 && ` (${flowSelection.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduleReportModal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        schedule={report?.schedule}
        reportId={report?.id}
        onSaved={handleScheduleSave}
      />

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        assetType={ASSET_REPORT}
        assetId={report?.id}
        assetName={report?.name}
        ownerName={report?.createdBy}
        ownerId={report?.createdById}
      />

      <ReportExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        report={report}
      />
    </div>
  );
};

export default ReportViewPage;
